import { Cookies } from "react-cookie";
// import testCases from "~/utils/Analytics/test_cases";
import { addDays, addWeeks } from "~/utils/dates";

const cookies = new Cookies();

const permanentCookies = [
  "lastLoggedUser",
  "user_attributes_persist",
  // ...Object.keys(testCases), // TODO: Add test_cases utils
];

const isSecure =
  import.meta.env.MODE === "test"
    ? false
    : import.meta.env.VITE_COOKIES_SECURE === "true";

const cookiesConfig = {
  path: "/",
  secure: isSecure,
};

/**************  GENERAL ****************************/

export const getKey = (cookieKey) => cookies.get(cookieKey);

export const setKey = (key, value, options = {}) => {
  if (["storeOrder", "shipToState", "user"].includes(key) && !options.expires) {
    options.expires = addWeeks(new Date(), 1);
  }
  cookies.set(key, value, { ...cookiesConfig, ...options });
  return value;
};

export const setPermanentKey = (key, value) => {
  // setting max age to 400 days to avoid removal upon session close
  setKey(key, value, { maxAge: 34560000 });
};

export const removeKey = (key) => {
  cookies.remove(key, { path: cookiesConfig.path });
  return true;
};

export const removeAllCookies = () => {
  return Object.keys(cookies.getAll())
    .filter((key) => !permanentCookies.includes(key))
    .forEach((key) => removeKey(key));
};

export const setLocalStorage = (key, payload) => {
  try {
    if (typeof window === "undefined") return null;
    const jsonString = JSON.stringify(payload);
    localStorage.setItem(key, jsonString);
  } catch (ex) {
    console.error(`[setLocalStorage] Error saving key "${key}":`, ex);
  }
};

export const getFromLocalStorage = (key) => {
  try {
    if (typeof window === "undefined") return null;
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (ex) {
    console.error(`[getFromLocalStorage] Error getting key "${key}":`, ex);
    return null;
  }
};

export const removeLocalStorageKey = (key) => {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.removeItem(key);
  } catch (ex) {
    console.log(ex);
  }
};

/**************  USER ****************************/

export const getUser = () => {
  let user = getKey("user");
  if (typeof user === "string") {
    const fixUser = JSON.parse(user);
    if (typeof fixUser.user === "object") {
      user = {
        email: fixUser.user.email,
        ship_address: fixUser.ship_address,
        id: fixUser.user.id,
        spree_api_key: fixUser.user.spree_api_key,
        name: fixUser.user.name,
      };
      setKey("user", user);
    }
  }
  return user || {};
};

export const getStoragedUser = () => {
  // First try to get user from cookies
  const cookieUser = getUser();
  if (cookieUser?.email) {
    return cookieUser;
  }

  // If no user in cookies, try localStorage (customerData)
  const customerData = getFromLocalStorage("customerData");
  if (customerData?.email) {
    return {
      email: customerData.email,
      id: customerData.id,
      name: `${customerData.firstName} ${customerData.lastName}`.trim(),
    };
  }

  return {};
};

export const setUserCookie = (user, expires = addWeeks(new Date(), 1)) => {
  setKey("user", user, { expires });
  setPermanentKey("lastLoggedUser", user?.id);
};

export const getApiKey = () => (getUser() || {}).spree_api_key;

export const getUserAttributesExpireTime = () => addDays(new Date(), 1);

/**************  Braze ****************************/

export const setBrazeToken = (token) => {
  setKey("braze_token", token, { expires: addDays(new Date(), 7) });
};

/**************  STORE ****************************/

export const cookieOrder = ({ token, number }) => ({ token, number });

/**************  QUIZ ****************************/

export const cookieIntroOrder = ({ token, number, line_items, item_count }) => {
  const variants = line_items
    .map((sp) => sp.variant_id)
    .sort()
    .join();
  return { token, number, variants, item_count };
};

export default getApiKey;
